// A "queued" share acceptance: the recipient clicked "Add to my courses" but
// still needs to sign in and/or subscribe first. We stash the intent here so it
// survives the OAuth + Stripe redirects and auto-fires the moment they land back
// eligible — no second click. Owned by PendingShareResumer (mounted at app root),
// which fires the accept whenever the user becomes authenticated + subscribed.
const KEY = 'graspd:pendingShareAccept';
const TTL_MS = 60 * 60 * 1000; // 1 hour — long enough to finish signup + checkout.

// Fired on set/clear so the resumer can react even when there's no auth/sub
// state change (e.g. an already-subscribed user clicking "Add to my courses").
export const PENDING_SHARE_EVENT = 'graspd:pendingShareChanged';

function emitChange() {
  try {
    window.dispatchEvent(new Event(PENDING_SHARE_EVENT));
  } catch {
    // No window (SSR/prerender) — nothing listening anyway.
  }
}

// uid is the signed-in user the intent was queued for, or null when queued
// anonymously (the normal pre-login flow — resumable by whoever signs in
// next). Stamping it lets getPendingShareAccept refuse to hand the token to
// a *different* signed-in user later — e.g. a shared device where a second
// person signs in without the first explicitly logging out, so the
// USER_DATA_KEYS-on-logout clear below never ran.
export function setPendingShareAccept(token, { uid = null } = {}) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ token, ts: Date.now(), uid }));
    emitChange();
  } catch {
    // Storage unavailable — the feature just degrades to a manual second click.
  }
}

export function getPendingShareAccept({ uid = null } = {}) {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { token, ts, uid: queuedFor } = JSON.parse(raw);
    if (!token || !ts || Date.now() - ts > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (queuedFor && queuedFor !== uid) {
      // Refuse, but do NOT delete. A read can legitimately arrive before auth
      // has resolved (uid still null) — e.g. returning from Stripe — and
      // clearing here would silently destroy a token the rightful owner is
      // about to redeem, including one they just paid for. Logout already
      // clears this key via USER_DATA_KEYS in lib/http.js, which is the
      // shared-device case the uid stamp is actually guarding.
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function clearPendingShareAccept() {
  try {
    localStorage.removeItem(KEY);
    emitChange();
  } catch {
    // no-op
  }
}
